import React, { useEffect, useMemo, useState } from 'react';
import { TextField } from '@/shared/components/TextField';
import { Snackbar } from '@/shared/components/Snackbar';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import CreateLayout from '@/shared/layout/createLayout';
import ToggleButton from '@/shared/components/ToggleButton/ToggleButton';
import { ProfilePictureUpload } from '@/shared/components/ProfilePictureUpload';
import { ProfilePicturePreview } from '@/shared/components/ProfilePicturePreview';
import Loader from '@/app/routes/Loader';
import { translateRole } from '@/shared/utils/roleTranslation';
import { getUserInitials } from '../../hooks/useViewProfile';
import {
  useEditProfile,
  type EditProfileErrors,
} from '../../hooks/useEditProfile';
import { DatePickerField } from '@/shared/components/DatePickerField';

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider = () => <div className="border-b border-[#DCDDE0]" />;

const formatCurrency = (currency: string | null | undefined): string => {
  if (!currency) {
    return '—';
  }
  if (currency.toUpperCase() === 'PLN') {
    return 'zł - Złoty polski (PLN)';
  }
  return currency;
};

// ─── Section Heading ──────────────────────────────────────────────────────────

const SectionHeading = ({ title }: { title: string }) => (
  <p className="text-[18px] text-on-surface-variant">{title}</p>
);

// ─── EditProfile ──────────────────────────────────────────────────────────────

const EditProfile: React.FC = () => {
  const {
    originalUser,
    isLoading,
    isSubmitting,
    formData,
    errors,
    handleFieldChange,
    handleNipChange,
    profileFile,
    existingProfileUrl,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    signatureFile,
    existingSignatureUrl,
    existingSignatureMeta,
    signatureUploadProgress,
    handleSignatureFileSelect,
    handleSignatureExistingDelete,
    handleSignatureError,
    contractFiles,
    existingContractDocs,
    handleContractFilesSelect,
    handleContractExistingDelete,
    handleContractError,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    isSnackbarOpen,
    snackbarError,
    handleCloseSnackbar,
    submitRequestedRef,
  } = useEditProfile();

  const role = originalUser?.role ?? '';
  const isClient = role === 'CLIENT';
  const isTechnician = role === 'TECHNICIAN';

  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
  const [profileFilePreviewUrl, setProfileFilePreviewUrl] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!profileFile) {
      setProfileFilePreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(profileFile);
    setProfileFilePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profileFile]);

  const previewImageUrl = useMemo(
    () => profileFilePreviewUrl ?? existingProfileUrl ?? null,
    [profileFilePreviewUrl, existingProfileUrl]
  );

  const isActive = originalUser?.status === 'active';

  // Scroll to first error field after failed submit
  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    const fieldOrder: (keyof EditProfileErrors)[] = [
      'name',
      'phoneNumber',
      'addressLine1',
      'city',
      'state',
      'nip',
      'contractDocs',
      'signature',
    ];
    const firstErrorField = fieldOrder.find((f) => Boolean(errors[f]));
    if (!firstErrorField) {
      return;
    }

    document
      .querySelector(`[data-field="profile-${firstErrorField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors, submitRequestedRef]);

  const Header = () => (
    <div className="flex items-center justify-between">
      <p className="text-titleLarge text-neutral-10">Edytuj profil</p>
      <div className="flex items-center gap-3 pr-7">
        <span
          className="text-on-surface-variant"
          style={{ fontSize: '12px', lineHeight: '20px' }}
        >
          Status:
        </span>
        <ToggleButton
          checked={isActive}
          label={isActive ? 'Aktywny' : 'Nieaktywny'}
          disabled
        />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  const initials = getUserInitials(formData.name || originalUser?.name);
  const maxContractFiles = 2;

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={false}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError=""
        Header={Header}
        submitButtonLabel="Zapisz"
        submitButtonLoadingLabel="Zapisywanie..."
        cancelButtonLabel="Anuluj"
        submitButtonDisabled={isSubmitting}
      >
        {/* ── Podstawowe dane ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <SectionHeading title="Podstawowe dane" />

          <div className="flex flex-col gap-8">
            {/* Zdjęcie profilowe */}
            <div>
              <ProfilePictureUpload
                onFileChange={handleProfileFileChange}
                onDelete={handleProfileFileDelete}
                onError={handleProfileFileError}
                onPreviewClick={
                  previewImageUrl
                    ? () => setIsProfilePreviewOpen(true)
                    : undefined
                }
                disabled={isSubmitting}
                initialLetter={initials}
                profileFile={profileFile ?? undefined}
                existingImageUrl={existingProfileUrl ?? undefined}
              />
            </div>

            {/* Field rows */}
            <div className="flex flex-col gap-8">
              {/* Wiersz 1: Imię i nazwisko + Adres e-mail + Numer telefonu */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="min-w-0" data-field="profile-name">
                  <TextField
                    fullWidth
                    label="Imię i nazwisko"
                    autoTrimMaxLength={200}
                    value={formData.name}
                    onChange={handleFieldChange('name')}
                    error={Boolean(errors.name)}
                    errorMessage={errors.name ?? ''}
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>
                <div className="min-w-0">
                  <TextField
                    fullWidth
                    label="Adres e-mail"
                    value={formData.email}
                    disabled
                  />
                </div>
                <div className="min-w-0" data-field="profile-phoneNumber">
                  <TextField
                    fullWidth
                    label="Numer telefonu"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleFieldChange('phoneNumber')}
                    error={Boolean(errors.phoneNumber)}
                    errorMessage={errors.phoneNumber ?? ''}
                    disabled={isSubmitting}
                    maxLength={9}
                  />
                </div>
              </div>

              {/* Wiersz 2: Waluta (tylko klient) */}
              {isClient && (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <TextField
                      fullWidth
                      label="Preferowana waluta"
                      value={formatCurrency(formData.preferredCurrency)}
                      disabled
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Divider />

        {/* ── Dane adresowe ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <SectionHeading title="Dane adresowe" />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="min-w-0" data-field="profile-addressLine1">
              <TextField
                fullWidth
                label="Adres (linia 1)"
                autoTrimMaxLength={200}
                value={formData.addressLine1}
                onChange={handleFieldChange('addressLine1')}
                error={Boolean(errors.addressLine1)}
                errorMessage={errors.addressLine1 ?? ''}
                disabled={isSubmitting}
              />
            </div>
            <div className="min-w-0">
              <TextField
                fullWidth
                label={
                  <>
                    Adres (linia 2)
                    <span className="text-[12px] font-normal">
                      {' '}
                      (Opcjonalne)
                    </span>
                  </>
                }
                dataTestId="textfield-Adres (linia 2) (Opcjonalne)"
                autoTrimMaxLength={200}
                value={formData.addressLine2}
                onChange={handleFieldChange('addressLine2')}
                disabled={isSubmitting}
              />
            </div>
            <div className="min-w-0" data-field="profile-city">
              <TextField
                fullWidth
                label="Miejscowość"
                autoTrimMaxLength={200}
                value={formData.city}
                onChange={handleFieldChange('city')}
                error={Boolean(errors.city)}
                errorMessage={errors.city ?? ''}
                disabled={isSubmitting}
              />
            </div>
            <div className="min-w-0" data-field="profile-state">
              <TextField
                fullWidth
                label="Województwo"
                autoTrimMaxLength={200}
                value={formData.state}
                onChange={handleFieldChange('state')}
                error={Boolean(errors.state)}
                errorMessage={errors.state ?? ''}
                disabled={isSubmitting}
              />
            </div>
            <div className="min-w-0">
              <TextField
                fullWidth
                label={
                  <>
                    Kod pocztowy
                    <span className="text-[12px] font-normal">
                      {' '}
                      (Opcjonalne)
                    </span>
                  </>
                }
                dataTestId="textfield-Kod pocztowy (Opcjonalne)"
                autoTrimMaxLength={20}
                value={formData.zipCode}
                onChange={handleFieldChange('zipCode')}
                disabled={isSubmitting}
              />
            </div>
            <div className="min-w-0">
              <TextField
                fullWidth
                label="Kraj"
                value={formData.country}
                disabled
              />
            </div>
          </div>
        </div>

        {/* ── Dane podatkowe i zgodności (tylko klient) ─────────────────── */}
        {isClient && (
          <>
            <Divider />
            <div className="flex flex-col gap-2">
              <SectionHeading title="Dane podatkowe i zgodności" />
              <div className="flex flex-col gap-8">
                {/* NIP */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="min-w-0" data-field="profile-nip">
                    <TextField
                      fullWidth
                      label="NIP"
                      autoTrimMaxLength={13}
                      value={formData.nip}
                      onChange={handleNipChange}
                      error={Boolean(errors.nip)}
                      errorMessage={errors.nip ?? ''}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Szczegóły umowy — przesyłanie plików */}
                <div
                  className="flex flex-col gap-2"
                  data-field="profile-contractDocs"
                >
                  <p className="text-[16px] font-normal text-on-surface-variant">
                    Dane umowy
                  </p>
                  <div className="w-full lg:w-[652px]">
                    <FileUpload
                      onFileSelect={handleContractFilesSelect}
                      selectedFiles={contractFiles}
                      existingFiles={existingContractDocs}
                      onExistingFileDelete={handleContractExistingDelete}
                      allowedTypes={[
                        FileType.PDF,
                        FileType.PNG,
                        FileType.JPG,
                        FileType.JPEG,
                      ]}
                      maxFiles={maxContractFiles}
                      onError={handleContractError}
                      disabled={isSubmitting}
                      error={Boolean(errors.contractDocs)}
                      errorMessage={errors.contractDocs}
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Szczegóły subskrypcji (tylko klient — tylko do odczytu) ────── */}
        {isClient && (
          <>
            <Divider />
            <div className="flex flex-col gap-2">
              <SectionHeading title="Szczegóły subskrypcji" />
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <TextField
                    fullWidth
                    label="Plan subskrypcji"
                    value={formData.subscriptionPlan || '—'}
                    disabled
                  />
                </div>
                <div className="min-w-0">
                  <TextField
                    fullWidth
                    label="Status płatności"
                    value={
                      formData.paymentStatus
                        ? ({ paid: 'Opłacono', unpaid: 'Nieopłacono' }[
                            formData.paymentStatus
                          ] ?? formData.paymentStatus)
                        : '—'
                    }
                    disabled
                  />
                </div>
                <div className="min-w-0 pointer-events-none">
                  <DatePickerField
                    label="Expiry Date"
                    value={
                      formData.expiryDate
                        ? new Date(formData.expiryDate)
                        : undefined
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Podpis (tylko technik) ────────────────────────────────────── */}
        {isTechnician && (
          <>
            <Divider />
            <div className="flex flex-col gap-2" data-field="profile-signature">
              <SectionHeading title="Podpis" />
              <div className="w-full lg:w-[652px]">
                <FileUpload
                  onFileSelect={handleSignatureFileSelect}
                  selectedFiles={signatureFile ? [signatureFile] : []}
                  existingFiles={
                    existingSignatureUrl && existingSignatureMeta
                      ? [
                          {
                            filename: existingSignatureMeta.filename,
                            fileSize: existingSignatureMeta.fileSize,
                            path: existingSignatureMeta.path,
                          },
                        ]
                      : []
                  }
                  onExistingFileDelete={handleSignatureExistingDelete}
                  allowedTypes={[
                    FileType.JPG,
                    FileType.JPEG,
                    FileType.PNG,
                    FileType.PDF,
                  ]}
                  maxFiles={1}
                  onError={handleSignatureError}
                  disabled={isSubmitting}
                  error={Boolean(errors.signature)}
                  errorMessage={errors.signature}
                  uploadProgress={signatureUploadProgress}
                  hideMaxFilesMessage
                  fullWidth
                />
              </div>
            </div>
          </>
        )}

        {/* ── Dostęp i uprawnienia ──────────────────────────────────────── */}
        <Divider />
        <div className="flex flex-col gap-2">
          <SectionHeading title="Dostęp i uprawnienia" />
          <div className="flex gap-4 w-[32%]">
            <TextField
              fullWidth
              label="Rola"
              value={translateRole(formData.role)}
              disabled
            />
          </div>
        </div>
      </CreateLayout>

      {/* Snackbar błędu */}
      <Snackbar
        open={isSnackbarOpen}
        message={snackbarError}
        variant="error"
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />

      {/* Profile picture preview modal */}
      {previewImageUrl && (
        <ProfilePicturePreview
          open={isProfilePreviewOpen}
          onClose={() => setIsProfilePreviewOpen(false)}
          imageUrl={previewImageUrl}
        />
      )}
    </>
  );
};

export default EditProfile;
